import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from '../components/Toast';
import { listOrders } from '../api/order.api';
import OrderStatusBadge from '../components/OrderStatusBadge';
import EmptyState from '../components/EmptyState';
import { resolveAssetUrl } from '../config';

const formatKES = (amount) => 'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const STATUS_TABS = [
    { label: 'All', value: '' },
    { label: 'Confirmed', value: 'confirmed' },
    { label: 'Processing', value: 'processing' },
    { label: 'Shipped', value: 'shipped' },
    { label: 'Delivered', value: 'delivered' },
    { label: 'Cancelled', value: 'cancelled' },
];

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeTab, setActiveTab] = useState('');

    useEffect(() => {
        document.title = 'My Orders | Fone Express';
    }, []);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, limit: 10 };
            if (activeTab) params.status = activeTab;
            const res = await listOrders(params);
            const resData = res?.data || res;
            if (resData?.isOk && resData?.data) {
                setOrders(resData.data.orders || []);
                setTotalPages(resData.data.pagination?.totalPages || resData.data.totalPages || 1);
            } else {
                setOrders([]);
                setTotalPages(1);
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Unable to load orders. Please try again.');
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    }, [page, activeTab]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleTabChange = (value) => {
        setActiveTab(value);
        setPage(1);
    };

    return (
        <div>
            {/* Page Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">My Orders</h1>
                <p className="text-muted-foreground text-sm mt-1">View and manage your orders</p>
            </div>

            {/* Status Filter Tabs */}
            <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4">
                {STATUS_TABS.map((tab) => (
                    <button
                        key={tab.value}
                        onClick={() => handleTabChange(tab.value)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab.value
                            ? 'bg-primary text-white'
                            : 'bg-muted text-muted-foreground hover:bg-border'
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Loading Skeleton */}
            {loading && (
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg p-4 animate-pulse">
                            <div className="flex justify-between items-start mb-3">
                                <div className="space-y-2">
                                    <div className="h-4 bg-muted rounded w-40"></div>
                                    <div className="h-3 bg-muted rounded w-24"></div>
                                </div>
                                <div className="h-6 bg-muted rounded-full w-20"></div>
                            </div>
                            <div className="flex gap-3 mb-3">
                                <div className="w-16 h-16 bg-muted rounded"></div>
                                <div className="w-16 h-16 bg-muted rounded"></div>
                            </div>
                            <div className="flex justify-between items-center">
                                <div className="h-4 bg-muted rounded w-28"></div>
                                <div className="h-8 bg-muted rounded w-24"></div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Error State */}
            {!loading && error && (
                <div className="text-center py-12">
                    <i className="ri-error-warning-line text-4xl text-red-400 mb-4 block"></i>
                    <p className="text-muted-foreground mb-4">{error}</p>
                    <button
                        onClick={fetchOrders}
                        className="px-6 py-2 bg-primary text-white rounded-lg hover:brightness-110 transition-all whitespace-nowrap"
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && orders.length === 0 && (
                <EmptyState
                    icon="ri-shopping-bag-line"
                    title="No orders yet"
                    description={activeTab ? `No ${activeTab} orders found.` : "You haven't placed any orders yet. Start shopping to see your orders here."}
                    actionLabel="Start Shopping"
                    actionLink="/products"
                />
            )}

            {/* Order List */}
            {!loading && !error && orders.length > 0 && (
                <div className="space-y-4">
                    {orders.map((order) => (
                        <div
                            key={order._id || order.orderNumber}
                            className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                        >
                            {/* Order Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                <div>
                                    <p className="font-semibold text-foreground">
                                        Order #{order.orderNumber}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {new Date(order.date || order.createdAt).toLocaleDateString('en-KE', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                        })}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <OrderStatusBadge status={order.status} />
                                    {order.delivery?.promisedText && (
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {order.delivery.promisedText}
                                        </span>
                                    )}
                                    {order.paymentMethod && (
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.paymentMethod === 'mpesa'
                                            ? 'bg-green-100 text-green-800'
                                            : order.paymentMethod === 'pesapal'
                                            ? 'bg-blue-100 text-blue-800'
                                            : order.paymentMethod === 'cod'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-purple-100 text-purple-800'
                                            }`}>
                                            {{ mpesa: 'M-PESA', pesapal: 'Pesapal', cod: 'COD', visa: 'Visa', mastercard: 'Mastercard', mosmos: 'Mosmos' }[order.paymentMethod] || order.paymentMethod.toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Item Thumbnails */}
                            {(order.items && order.items.length > 0) ? (
                                <div className="flex items-center gap-3 mb-3">
                                    {order.items.slice(0, 3).map((item, idx) => (
                                        <div
                                            key={idx}
                                            className="w-16 h-16 border border-border rounded-lg overflow-hidden bg-muted flex-shrink-0"
                                        >
                                            <img
                                                src={resolveAssetUrl(item.image || item.productImage)}
                                                alt={item.productName || 'Product'}
                                                className="w-full h-full object-contain p-1"
                                            />
                                        </div>
                                    ))}
                                    {order.items.length > 3 && (
                                        <span className="text-xs text-muted-foreground font-medium">
                                            +{order.items.length - 3} more
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-16 h-16 border border-border rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                                        <i className="ri-shopping-bag-line text-2xl text-muted-foreground/40"></i>
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {order.itemCount || 0} item{(order.itemCount || 0) !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            )}

                            {/* Order Footer */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t border-border">
                                <div className="flex items-center gap-4">
                                    <span className="text-sm text-muted-foreground">
                                        {order.itemCount || (order.items || []).length} item{(order.itemCount || (order.items || []).length) !== 1 ? 's' : ''}
                                    </span>
                                    <span className="font-semibold text-foreground">
                                        {formatKES(order.grandTotal || order.pricing?.grandTotal)}
                                    </span>
                                </div>
                                <Link
                                    to={`/account/orders/${order.orderNumber}`}
                                    className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:brightness-110 transition-all whitespace-nowrap text-center"
                                >
                                    View Details
                                </Link>
                            </div>
                        </div>
                    ))}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-3 pt-6">
                            <button
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="px-4 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors whitespace-nowrap"
                            >
                                <i className="ri-arrow-left-s-line mr-1"></i>
                                Previous
                            </button>
                            <span className="text-sm text-muted-foreground">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="px-4 py-2 border border-border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary transition-colors whitespace-nowrap"
                            >
                                Next
                                <i className="ri-arrow-right-s-line ml-1"></i>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
